import { useState } from "react";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type {
  TargetareServiciuMapare,
  TargetareSugestieMapare,
} from "@/types/targetare";
import { ETICHETE_AMBIGUITATE, ETICHETE_REGULA } from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import type { FirmeCriteriuDeschis } from "../FirmeCriteriuDialog";
import { EditorCoduri } from "./EditorCoduri";

type ServiciuMapareCardProps = {
  serviciu: TargetareServiciuMapare;
  onFirme: (deschis: FirmeCriteriuDeschis) => void;
  onAdaugaSugestie: (
    serviciu: TargetareServiciuMapare,
    sugestie: TargetareSugestieMapare
  ) => void;
  adaugaInCurs: boolean;
};

const CLASE_AMBIGUITATE: Record<string, string> = {
  Neambiguu: "bg-ds-green-50 text-ds-green-700 border-ds-green-200",
  Ambiguu: "bg-amber-50 text-amber-700 border-amber-200",
  Ierarhic: "bg-ds-blue-50 text-ds-blue-700 border-ds-blue-200",
  "N/A": "bg-muted text-muted-foreground",
};

function ContorFirme({
  eticheta,
  total,
  scop,
  onClick,
}: {
  eticheta: string;
  total: number;
  scop: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border px-2 py-1 text-left text-xs hover:bg-muted"
      title="Deschide firmele"
    >
      <span className="text-muted-foreground">{eticheta}: </span>
      <span className="font-semibold tabular-nums">{formatNumar(total)}</span>
      <span className="tabular-nums text-muted-foreground">
        {" "}
        ({formatNumar(scop)} în scop)
      </span>
    </button>
  );
}

export function ServiciuMapareCard({
  serviciu,
  onFirme,
  onAdaugaSugestie,
  adaugaInCurs,
}: ServiciuMapareCardProps) {
  const [detalii, setDetalii] = useState(false);
  const s = serviciu;
  const coduriPentru = (versiune: number, secundar: boolean) =>
    s.coduri.filter((c) => c.versiune === versiune && c.secundar === secundar);
  const numeCriteriu = (fel: "principal" | "secundar" | "lista") =>
    `${s.name}: firme potrivite ${
      fel === "principal"
        ? "prin CAEN principal"
        : fel === "secundar"
          ? "prin cod autorizat"
          : "de pe liste"
    }`;
  const areDescriere = Boolean(
    s.descriere_juridica ||
      s.descriere_practica ||
      s.note_dnsc ||
      s.nota ||
      s.justificare
  );

  return (
    <Card id={`serviciu-${s.entity_id}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1">
            <p className="font-heading font-semibold">
              <span className="mr-2 font-mono text-xs text-muted-foreground">
                {s.entity_id}
              </span>
              {s.name}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {s.anexa && (
                <Badge variant="outline" className="font-normal">
                  Anexa {s.anexa}
                </Badge>
              )}
              <Badge
                variant="outline"
                className={cn(
                  "font-normal",
                  CLASE_AMBIGUITATE[s.ambiguitate ?? ""] ?? ""
                )}
                title={s.justificare ?? undefined}
              >
                {ETICHETE_AMBIGUITATE[s.ambiguitate ?? "(fără)"] ??
                  s.ambiguitate}
              </Badge>
              {s.super_ambiguu && (
                <Badge variant="destructive" className="font-normal">
                  super-ambiguu
                </Badge>
              )}
              {s.regula_tip && (
                <span
                  className="text-muted-foreground"
                  title={ETICHETE_REGULA[s.regula_tip]}
                >
                  regula {s.regula_tip}
                  {s.orice_dimensiune && " · orice dimensiune"}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <ContorFirme
              eticheta="CAEN principal"
              total={s.firme_principal}
              scop={s.firme_principal_scop}
              onClick={() =>
                onFirme({
                  criteriu: {
                    tip: "serviciu",
                    entity_id: s.entity_id,
                    fel: "principal",
                  },
                  titlu: numeCriteriu("principal"),
                })
              }
            />
            <ContorFirme
              eticheta="Cod autorizat"
              total={s.firme_secundar}
              scop={s.firme_secundar_scop}
              onClick={() =>
                onFirme({
                  criteriu: {
                    tip: "serviciu",
                    entity_id: s.entity_id,
                    fel: "secundar",
                  },
                  titlu: numeCriteriu("secundar"),
                })
              }
            />
            <ContorFirme
              eticheta="Liste"
              total={s.firme_lista}
              scop={s.firme_lista_scop}
              onClick={() =>
                onFirme({
                  criteriu: {
                    tip: "serviciu",
                    entity_id: s.entity_id,
                    fel: "lista",
                  },
                  titlu: numeCriteriu("lista"),
                })
              }
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {[2, 3].map((versiune) => (
            <div key={versiune} className="space-y-3 rounded-md border p-3">
              <p className="text-sm font-medium">CAEN Rev. {versiune}</p>
              <EditorCoduri
                entityId={s.entity_id}
                serviciu={s.name}
                coloana={versiune === 2 ? "CAEN_rev_2" : "CAEN_rev_3"}
                coduri={coduriPentru(versiune, false)}
                onFirme={onFirme}
              />
              <EditorCoduri
                entityId={s.entity_id}
                serviciu={s.name}
                coloana={
                  versiune === 2
                    ? "caen_rev_2_secundare"
                    : "caen_rev_3_secundare"
                }
                coduri={coduriPentru(versiune, true)}
                onFirme={onFirme}
              />
            </div>
          ))}
        </div>

        {s.sugestii.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Sugestii din liste ({s.sugestii.length}): coduri principale ale
              firmelor de pe liste, nemapate pe serviciu
            </p>
            <div className="flex flex-wrap gap-1">
              {s.sugestii.slice(0, 12).map((sg) => (
                <span
                  key={`${sg.versiune}-${sg.cod}`}
                  className="inline-flex items-center gap-1 rounded border border-dashed px-1.5 py-0.5 text-xs"
                  title={`${sg.descriere ?? ""}\n${formatNumar(sg.firme_principal)} firme de pe liste cu acest CAEN principal (${formatNumar(sg.firme_principal_scop)} mari/mijlocii)`}
                >
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() =>
                      onFirme({
                        criteriu: {
                          tip: "caen",
                          cod: sg.cod,
                          versiune: sg.versiune,
                          fel: "principal",
                        },
                        titlu: `Firme cu CAEN principal ${sg.cod} (Rev. ${sg.versiune})${sg.descriere ? ` „${sg.descriere}”` : ""}`,
                      })
                    }
                  >
                    <span className="font-mono font-semibold">{sg.cod}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      R{sg.versiune}
                    </span>
                    <span className="ml-1 tabular-nums text-muted-foreground">
                      {formatNumar(sg.firme_principal_scop)}/
                      {formatNumar(sg.firme_principal)}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    title={`Adaugă ${sg.cod} ca secundar în Rev. ${sg.versiune}`}
                    disabled={adaugaInCurs}
                    onClick={() => onAdaugaSugestie(s, sg)}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {s.sugestii.length > 12 && (
                <span className="text-xs text-muted-foreground">
                  +{s.sugestii.length - 12} în pagina Matrice CAEN
                </span>
              )}
            </div>
          </div>
        )}

        {areDescriere && (
          <div>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setDetalii((v) => !v)}
            >
              {detalii ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              {detalii ? "Ascunde" : "Arată"} definiția și notele
            </button>
            {detalii && (
              <dl className="mt-2 grid gap-2 text-xs md:grid-cols-2">
                {s.descriere_juridica && (
                  <div>
                    <dt className="font-medium">Definiție juridică</dt>
                    <dd className="text-muted-foreground">
                      {s.descriere_juridica}
                    </dd>
                  </div>
                )}
                {s.descriere_practica && (
                  <div>
                    <dt className="font-medium">Descriere practică</dt>
                    <dd className="text-muted-foreground">
                      {s.descriere_practica}
                    </dd>
                  </div>
                )}
                {s.note_dnsc && (
                  <div>
                    <dt className="font-medium">Note DNSC</dt>
                    <dd className="text-muted-foreground">{s.note_dnsc}</dd>
                  </div>
                )}
                {s.justificare && (
                  <div>
                    <dt className="font-medium">Justificarea ambiguității</dt>
                    <dd className="text-muted-foreground">{s.justificare}</dd>
                  </div>
                )}
                {s.nota && (
                  <div>
                    <dt className="font-medium">Notă</dt>
                    <dd className="text-muted-foreground">{s.nota}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
